import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useGetActionRewardsQuery, useDeleteActionRewardCampaignsMutation } from 'reduxState/store/actionRewards/api';
import { IActionRewardCampaign, ICampaignData } from 'reduxState/store/actionRewards/type';
import { useGetUserApplicationsQuery } from 'reduxState/store/application/api';
import ActionRewardsTable from 'components/ActionRewards/ActionRewardsTable';
import ActionRewardsFilters from 'components/ActionRewards/ActionRewardsFilters';
import { IAppOption, TCampaignTypes } from 'components/ActionRewards/types';
import ActionRewardsModal from 'components/ActionRewards/ActionRewardsModal';
import ConfirmationModal from 'components/ConceptManager/ConfirmationModal';
import { formatLocalDateTime } from 'helpers/DateHelpers';

const ActionRewardsManager = () => {
  const [appIdsField, setAppIdsField] = useState<IAppOption[]>([]);
  const [campaignIdField, setCampaignIdField] = useState(0);
  const [actionRewardField, setActionRewardField] = useState<TCampaignTypes | ''>('');

  const [appIds, setAppIds] = useState('all');
  const [campaignId, setCampaignId] = useState(0);
  const [actionReward, setActionReward] = useState('');

  const [page, setPage] = useState(1);
  const [campaignData, setCampaignData] = useState<IActionRewardCampaign[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<IActionRewardCampaign | null>(null);
  const { data: applicationsData } = useGetUserApplicationsQuery();
  const applications = (applicationsData?.appGroups ?? []).flatMap((group: any) => group.applications || []);
  const [deleteActionRewardCampaigns] = useDeleteActionRewardCampaignsMutation();
  const { data, isFetching } = useGetActionRewardsQuery({
    appIds,
    page,
    campaignId,
    actionReward,
  });

  useEffect(() => {
    // If there is a campaignId, set the campaign data to the campaign
    if (campaignId > 0) {
      setCampaignData([data as IActionRewardCampaign]);
      // Otherwise, data should have actionRewardConfig array
    } else {
      setCampaignData((data as ICampaignData)?.actionRewardConfig || []);
    }
  }, [data, campaignId]);

  const handleApplyFilters = () => {
    setAppIds(appIdsField.map((app: IAppOption) => app.id).join(',') || 'all');
    setCampaignId(campaignIdField);
    setActionReward(actionRewardField);
    setPage(1);

    // If a campaign is selected, clear the appIds and actionReward fields
    if (campaignIdField > 0) {
      setAppIdsField([]);
      setActionRewardField('');
    }
  };

  const handleClearFilters = () => {
    setAppIdsField([]);
    setCampaignIdField(0);
    setActionRewardField('');

    setAppIds('all');
    setCampaignId(0);
    setActionReward('');
    setPage(1);
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    try {
      await deleteActionRewardCampaigns(selectedCampaign.id).unwrap();
      toast.success(`Campaign ${selectedCampaign.id} was deleted successfully!`);
      setIsConfirmationModalOpen(false);
      setSelectedCampaign(null);
    } catch (error) {
      toast.error('Failed to delete campaign');
      console.error(error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCampaign(null);
  };

  return (
    <div className="font-montserrat">
      <div className="flex flex-col items-start flex-1 max-w-[1400px] min-w-[500px] mx-auto">
        <h1 className="self-start text-muted-dark-purple">Action Rewards Manager</h1>
        <div className="w-full flex justify-end mb-4">
          <Button
            className="purple-rounded-button px-5 py-4 font-semibold !text-sm"
            onClick={() => setIsModalOpen(true)}
          >
            <AddIcon className="mr-2" />
            New Campaign
          </Button>
        </div>
        <ActionRewardsFilters
          appIds={appIdsField}
          setAppIds={setAppIdsField}
          campaignId={campaignIdField}
          setCampaignId={setCampaignIdField}
          actionReward={actionRewardField}
          setActionReward={setActionRewardField}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
        {!isEmpty(campaignData) ? (
          <ActionRewardsTable
            data={campaignData}
            applications={applications}
            isFetching={isFetching}
            totalPages={'pageInfo' in (data || {}) ? (data as ICampaignData).pageInfo?.TotalPages : 1}
            setPage={setPage}
            setIsModalOpen={setIsModalOpen}
            setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            setSelectedCampaign={setSelectedCampaign}
          />
        ) : (
          <div className="flex flex-col items-center justify-center w-full">
            <p>No campaigns found</p>
          </div>
        )}
        {isModalOpen && (
          <ActionRewardsModal
            applications={applications}
            isModalOpen={isModalOpen}
            selectedCampaign={selectedCampaign}
            handleCloseModal={handleCloseModal}
          />
        )}
        {isConfirmationModalOpen && selectedCampaign && (
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            title=""
            containerClassName="min-w-[500px]"
            buttonContainerClassName="flex justify-center"
            content={
              <div className="flex flex-col items-center justify-center mt-5">
                <div className="text-center w-full bg-light-grey rounded-lg p-4">
                  <p className="text-center w-full text-xs font-bold">
                    Application ID: <span className="font-normal">{selectedCampaign?.applicationID}</span>
                  </p>
                  <p className="text-center w-full text-xs font-bold">
                    Campaign Type: <span className="font-normal">{selectedCampaign?.actionReward}</span>
                  </p>
                  <p className="text-center w-full text-xs font-bold">
                    Bonus Amount: <span className="font-normal">{selectedCampaign?.bonusAmount}</span>
                  </p>
                  <p className="text-center w-full text-xs font-bold">
                    Date Range:{' '}
                    <span className="font-normal">
                      {`${formatLocalDateTime(selectedCampaign?.startDate, 'M/dd/yyyy')} -
                        ${formatLocalDateTime(selectedCampaign?.endDate, 'M/dd/yyyy')}`}
                    </span>
                  </p>
                </div>
                <p className="w-full text-center mt-4">
                  Are you sure you want to delete Campaign ID {selectedCampaign?.id}?
                </p>
              </div>
            }
            onClose={() => {
              setIsConfirmationModalOpen(false);
              setSelectedCampaign(null);
            }}
            onConfirm={() => handleDeleteCampaign()}
          />
        )}
      </div>
    </div>
  );
};

export default ActionRewardsManager;
